/**
 * Mixpanel Analytics Integration
 *
 * Tracks user access, authentication, and feature usage for the Nigastock Trader demo.
 * Allows stakeholders like Ibukun Awosika to see engagement metrics.
 */

import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = 'f5335f366ff21ac6eb3baa93d124c8f0';
let isInitialized = false;

/**
 * Initialize Mixpanel (call once on app startup)
 */
export function initAnalytics() {
  if (isInitialized) return;

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: false,
      track_pageview: true,
      persistence: 'localStorage',
    });
    isInitialized = true;
    console.log('✅ Mixpanel analytics initialized');
  } catch (err) {
    console.warn('⚠️ Mixpanel initialization failed:', err);
  }
}

/**
 * Track user login with their details
 */
export function trackLogin(username: string, email: string) {
  if (!isInitialized) return;

  try {
    mixpanel.identify(email);
    mixpanel.people.set({
      $email: email,
      $name: username,
      'First Login': new Date().toISOString(),
      'Demo Access': true,
    });

    mixpanel.track('User Login', {
      username,
      email,
      timestamp: new Date().toISOString(),
    });

    console.log(`📊 Tracked login for ${username}`);
  } catch (err) {
    console.warn('⚠️ Login tracking failed:', err);
  }
}

/**
 * Track page/view navigation
 */
export function trackPageView(viewName: string, viewType: 'trader' | 'dashboard' | 'research' | 'portfolio' | 'settings') {
  if (!isInitialized) return;

  try {
    mixpanel.track('Page View', {
      page: viewName,
      type: viewType,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('⚠️ Page view tracking failed:', err);
  }
}

/**
 * Track feature usage (e.g., placing trades, viewing ratings)
 */
export function trackFeatureUsage(featureName: string, metadata?: Record<string, any>) {
  if (!isInitialized) return;

  try {
    mixpanel.track('Feature Usage', {
      feature: featureName,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('⚠️ Feature tracking failed:', err);
  }
}

/**
 * Track specific actions (e.g., opening Ratings, viewing a stock)
 */
export function trackAction(action: string, details?: Record<string, any>) {
  if (!isInitialized) return;

  try {
    mixpanel.track(action, {
      ...details,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('⚠️ Action tracking failed:', err);
  }
}

/**
 * Track session duration and exit
 */
export function trackLogout(sessionDurationMinutes: number) {
  if (!isInitialized) return;

  try {
    mixpanel.track('User Logout', {
      'Session Duration (minutes)': sessionDurationMinutes,
      timestamp: new Date().toISOString(),
    });
  } catch (err) {
    console.warn('⚠️ Logout tracking failed:', err);
  }
}

/**
 * Increment a user property (e.g., number of trades viewed)
 */
export function incrementUserStat(statName: string, value: number = 1) {
  if (!isInitialized) return;

  try {
    mixpanel.people.increment(statName, value);
  } catch (err) {
    console.warn('⚠️ Stat increment failed:', err);
  }
}

/**
 * Get Mixpanel instance for advanced usage
 */
export function getMixpanel() {
  return mixpanel;
}
